import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import classNames from "../../../utils/classNames";
import DateSeparator from "../DateSeparator";
import { readDateSeparatorMetadata, resolveStickyDateLabel } from "../../../utils/dateLabel";
import "./styles.scss";

const DATE_SEPARATOR_SELECTOR = "[data-date-iso], [data-date-label], [data-date]";
const DATE_SEPARATOR_WRAPPER_SELECTOR = "[data-date-separator='true']";
const HIDDEN_SEPARATOR_CLASSNAME = "is-hidden-by-sticky";
const DEFAULT_STICKY_TOP_PX = 8;
const DEFAULT_PILL_HEIGHT_PX = 30;
const INLINE_HIDE_PADDING_PX = 4;
const TOP_BOUNDARY_HIDE_EXTRA_PX = 40;
const PUSH_OFFSET_PRECISION_PX = 1;
// Distance (px) the active separator can travel past the sticky lane before its
// pill fully crossfades from docked-dark back to floating-glass.
const DOCK_FADE_RANGE_PX = 56;

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const normalizeText = (value) => (typeof value === "string" ? value.trim() : "");

const findActiveIndex = (separators, targetTop) => {
    if (!Array.isArray(separators) || separators.length === 0) {
        return -1;
    }

    let low = 0;
    let high = separators.length - 1;
    let answer = -1;

    while (low <= high) {
        const middle = Math.floor((low + high) / 2);
        if (separators[middle].top <= targetTop) {
            answer = middle;
            low = middle + 1;
        } else {
            high = middle - 1;
        }
    }

    return answer < 0 ? 0 : answer;
};

const pickDateSeparatorNodes = (container) => {
    if (!container) {
        return [];
    }

    const candidateNodes = Array.from(container.querySelectorAll(DATE_SEPARATOR_SELECTOR));

    if (!candidateNodes.length) {
        return [];
    }

    const seen = new Set();
    const uniqueNodes = [];

    candidateNodes.forEach((candidateNode) => {
        const normalizedNode = candidateNode.closest(DATE_SEPARATOR_WRAPPER_SELECTOR) || candidateNode;
        if (!normalizedNode || seen.has(normalizedNode)) {
            return;
        }

        const meta = readDateSeparatorMetadata(normalizedNode);
        if (!meta.isoDate && !meta.label) {
            return;
        }

        seen.add(normalizedNode);
        uniqueNodes.push(normalizedNode);
    });

    return uniqueNodes;
};

const buildSeparatorDateKey = (isoDate, label, fallbackIndex = 0) => {
    const normalizedIndex = Number.isInteger(fallbackIndex) ? fallbackIndex : 0;
    const normalizedIso = typeof isoDate === "string" ? isoDate.trim() : "";
    if (normalizedIso) {
        return `iso:${normalizedIso}:${normalizedIndex}`;
    }

    const normalizedLabel = typeof label === "string" ? label.trim() : "";
    if (normalizedLabel) {
        return `label:${normalizedLabel}:${normalizedIndex}`;
    }

    return `date:${normalizedIndex}`;
};

const buildDateIdentityKey = ({ isoDate, label, dateKey } = {}) => {
    const normalizedIso = normalizeText(isoDate);
    if (normalizedIso) {
        return `iso:${normalizedIso}`;
    }
    const normalizedLabel = normalizeText(label);
    if (normalizedLabel) {
        return `label:${normalizedLabel}`;
    }
    const normalizedDateKey = normalizeText(dateKey);
    if (normalizedDateKey) {
        return `key:${normalizedDateKey}`;
    }
    return "";
};

const areDateIdentitiesEqual = (left, right) => {
    if (!left || !right) {
        return false;
    }

    const leftIso = normalizeText(left.isoDate);
    const rightIso = normalizeText(right.isoDate);
    if (leftIso && rightIso) {
        return leftIso === rightIso;
    }

    const leftLabel = normalizeText(left.label);
    const rightLabel = normalizeText(right.label);
    if (leftLabel && rightLabel) {
        return leftLabel === rightLabel;
    }

    const leftDateKey = normalizeText(left.dateKey);
    const rightDateKey = normalizeText(right.dateKey);
    if (leftDateKey && rightDateKey) {
        return leftDateKey === rightDateKey;
    }

    return false;
};

const resolveSeparatorMeasureNode = (node) => {
    if (!node || typeof node.querySelector !== "function") {
        return node;
    }
    return node.querySelector(".message-date-separator") || node;
};

const measureSeparatorPlacement = (node, fallbackHeight = DEFAULT_PILL_HEIGHT_PX) => {
    // Read offset metrics once in the measurement phase and keep scroll updates geometry-only.
    const measuredTop = Number(node?.offsetTop);
    const fallbackMeasuredHeight = Number(node?.offsetHeight);
    const measureNode = resolveSeparatorMeasureNode(node);
    const measuredNodeHeight = Number(measureNode?.offsetHeight);

    const resolvedTop = Number.isFinite(measuredTop) ? measuredTop : 0;
    const resolvedHeight = Number.isFinite(measuredNodeHeight) && measuredNodeHeight > 0
        ? measuredNodeHeight
        : (Number.isFinite(fallbackMeasuredHeight) && fallbackMeasuredHeight > 0
            ? fallbackMeasuredHeight
            : fallbackHeight);

    return {
        top: resolvedTop,
        height: Math.max(1, resolvedHeight),
    };
};

const isSeparatorWithinTopBoundaryHideZone = ({
    separator,
    scrollTop,
    stickyTop,
    pillHeight,
}) => {
    if (!separator) {
        return false;
    }

    const separatorTop = separator.top - scrollTop;
    const topBoundaryBottom = stickyTop + pillHeight + INLINE_HIDE_PADDING_PX + TOP_BOUNDARY_HIDE_EXTRA_PX;
    return separatorTop <= topBoundaryBottom;
};

const areSeparatorMeasurementsEqual = (left, right) => {
    if (left === right) {
        return true;
    }
    if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) {
        return false;
    }

    for (let index = 0; index < left.length; index += 1) {
        const prev = left[index];
        const next = right[index];
        if (!prev || !next) {
            return false;
        }
        if (prev.node !== next.node || prev.dateKey !== next.dateKey) {
            return false;
        }
        if (prev.isoDate !== next.isoDate || prev.label !== next.label) {
            return false;
        }
        if (Math.abs((prev.top || 0) - (next.top || 0)) > 0.5) {
            return false;
        }
        if (Math.abs((prev.height || 0) - (next.height || 0)) > 0.5) {
            return false;
        }
    }

    return true;
};

const isSeparatorOverlappingStickyLane = ({
    separator,
    scrollTop,
    stickyTop,
    pillHeight,
}) => {
    if (!separator) {
        return false;
    }

    const separatorTop = separator.top - scrollTop;
    const separatorHeight = Math.max(1, Number(separator.height) || pillHeight || DEFAULT_PILL_HEIGHT_PX);
    const separatorBottom = separatorTop + separatorHeight;
    const overlapZoneTop = stickyTop - INLINE_HIDE_PADDING_PX;
    const overlapZoneBottom = stickyTop + pillHeight + INLINE_HIDE_PADDING_PX;

    return separatorTop <= overlapZoneBottom && separatorBottom >= overlapZoneTop;
};

const areStateValuesEqual = (left, right) => (
    left.activeIndex === right.activeIndex
    && left.activeDateKey === right.activeDateKey
    && left.activeLabel === right.activeLabel
    && left.activeIso === right.activeIso
    && left.shouldHideInlineActiveSeparator === right.shouldHideInlineActiveSeparator
    && left.nextDateKey === right.nextDateKey
    && left.nextLabel === right.nextLabel
    && left.nextIso === right.nextIso
    && left.shouldHideInlineNextSeparator === right.shouldHideInlineNextSeparator
    && left.pushOffsetPx === right.pushOffsetPx
    && left.activeDockProgress === right.activeDockProgress
);

const EMPTY_STICKY_STATE = {
    activeIndex: -1,
    activeDateKey: "",
    activeLabel: "",
    activeIso: "",
    shouldHideInlineActiveSeparator: false,
    nextDateKey: "",
    nextLabel: "",
    nextIso: "",
    shouldHideInlineNextSeparator: false,
    pushOffsetPx: 0,
    activeDockProgress: 0,
};

const resolveScrollContainer = (scrollRef, scrollContainer) => {
    if (scrollContainer) {
        return scrollContainer;
    }
    return scrollRef?.current ?? null;
};

const StickyDateHeader = ({
    scrollRef = null,
    scrollContainer = null,
    className = "",
    stickyTopPx = DEFAULT_STICKY_TOP_PX,
    recalculateKey = 0,
}) => {
    const [stickyState, setStickyState] = useState(EMPTY_STICKY_STATE);
    const [pillHeightPx, setPillHeightPx] = useState(DEFAULT_PILL_HEIGHT_PX);

    const separatorsRef = useRef([]);
    const separatorsByIdentityRef = useRef(new Map());
    const scrollRafRef = useRef(null);
    const recalcRafRef = useRef(null);
    const scrollIdleTimeoutRef = useRef(null);
    const hiddenSeparatorNodesRef = useRef(new Set());
    const activePillRef = useRef(null);
    const setActivePillRef = useCallback((node) => {
        activePillRef.current = node || null;
    }, []);

    const applyHiddenSeparatorClassNames = useCallback((nextHiddenNodes) => {
        const previousHiddenNodes = hiddenSeparatorNodesRef.current;
        if (previousHiddenNodes.size === nextHiddenNodes.size) {
            let isEqual = true;
            for (const node of previousHiddenNodes) {
                if (!nextHiddenNodes.has(node)) {
                    isEqual = false;
                    break;
                }
            }
            if (isEqual) {
                return;
            }
        }

        previousHiddenNodes.forEach((node) => {
            if (!nextHiddenNodes.has(node) && node?.classList) {
                node.classList.remove(HIDDEN_SEPARATOR_CLASSNAME);
            }
        });

        nextHiddenNodes.forEach((node) => {
            if (!previousHiddenNodes.has(node) && node?.classList) {
                node.classList.add(HIDDEN_SEPARATOR_CLASSNAME);
            }
        });

        hiddenSeparatorNodesRef.current = nextHiddenNodes;
    }, []);

    const clearHiddenSeparatorClassNames = useCallback(() => {
        applyHiddenSeparatorClassNames(new Set());
    }, [applyHiddenSeparatorClassNames]);

    const updateHiddenSeparatorClassNames = useCallback(({
        activeSeparator,
        nextSeparator,
        shouldHideInlineActiveSeparator,
        shouldHideInlineNextSeparator,
        scrollTop,
        stickyTop,
        pillHeight,
    }) => {
        if (!activeSeparator && !nextSeparator) {
            clearHiddenSeparatorClassNames();
            return;
        }

        const shouldHideNextSeparator = shouldHideInlineNextSeparator
            && nextSeparator
            && !areDateIdentitiesEqual(activeSeparator, nextSeparator);

        if (!shouldHideInlineActiveSeparator && !shouldHideNextSeparator) {
            clearHiddenSeparatorClassNames();
            return;
        }

        const nextHiddenNodes = new Set();
        const maybeHideSeparator = (separator, shouldHide, forceHide = false) => {
            if (!shouldHide) {
                return;
            }
            const node = separator?.node;
            if (!node || !node.classList) {
                return;
            }
            if (!forceHide && !isSeparatorOverlappingStickyLane({
                separator,
                scrollTop,
                stickyTop,
                pillHeight,
            })) {
                return;
            }
            nextHiddenNodes.add(node);
        };

        maybeHideSeparator(activeSeparator, shouldHideInlineActiveSeparator, true);

        if (shouldHideInlineActiveSeparator) {
            const activeIdentityKey = buildDateIdentityKey(activeSeparator);
            const sameDateSeparators = activeIdentityKey
                ? (separatorsByIdentityRef.current.get(activeIdentityKey) || [])
                : [];

            sameDateSeparators.forEach((separator) => {
                if (!separator || separator === activeSeparator) {
                    return;
                }
                if (!isSeparatorOverlappingStickyLane({
                    separator,
                    scrollTop,
                    stickyTop,
                    pillHeight,
                }) && !isSeparatorWithinTopBoundaryHideZone({
                    separator,
                    scrollTop,
                    stickyTop,
                    pillHeight,
                })) {
                    return;
                }
                maybeHideSeparator(separator, true, true);
            });
        }

        maybeHideSeparator(nextSeparator, shouldHideNextSeparator);

        applyHiddenSeparatorClassNames(nextHiddenNodes);
    }, [applyHiddenSeparatorClassNames, clearHiddenSeparatorClassNames]);

    const updateStickyState = useCallback(() => {
        const container = resolveScrollContainer(scrollRef, scrollContainer);
        const separators = separatorsRef.current;

        if (!container || !separators.length) {
            separatorsByIdentityRef.current = new Map();
            clearHiddenSeparatorClassNames();
            setStickyState((previous) => (areStateValuesEqual(previous, EMPTY_STICKY_STATE) ? previous : EMPTY_STICKY_STATE));
            return;
        }

        const scrollTop = container.scrollTop;
        const normalizedStickyTop = Math.max(0, Number(stickyTopPx) || 0);
        const normalizedPillHeight = Math.max(1, Number(pillHeightPx) || DEFAULT_PILL_HEIGHT_PX);
        const activeIndex = findActiveIndex(separators, scrollTop + normalizedStickyTop);
        const activeSeparator = separators[activeIndex] || null;

        if (!activeSeparator) {
            clearHiddenSeparatorClassNames();
            setStickyState((previous) => (areStateValuesEqual(previous, EMPTY_STICKY_STATE) ? previous : EMPTY_STICKY_STATE));
            return;
        }

        const nextSeparator = separators[activeIndex + 1] || null;
        let pushOffsetPx = 0;

        if (nextSeparator) {
            const nextTopInScroll = nextSeparator.top - scrollTop;
            const nextTopInOverlay = nextTopInScroll;
            const collisionLine = normalizedStickyTop + normalizedPillHeight;
            pushOffsetPx = clamp(collisionLine - nextTopInOverlay, 0, normalizedPillHeight);
        }

        const roundedPushOffsetPx = Math.round(pushOffsetPx * 100) / 100;
        const normalizedPushOffsetPx = Math.round(roundedPushOffsetPx / PUSH_OFFSET_PRECISION_PX) * PUSH_OFFSET_PRECISION_PX;

        // Docking progress: 1 when the active separator sits at the sticky lane
        // (pill fixed in place -> dark), fading to 0 as it scrolls past into the
        // day's messages (pill floats -> glass). Quantized to limit re-renders.
        const distancePastLanePx = (scrollTop + normalizedStickyTop) - activeSeparator.top;
        const rawDockProgress = clamp(1 - (distancePastLanePx / DOCK_FADE_RANGE_PX), 0, 1);
        const activeDockProgress = Math.round(rawDockProgress * 50) / 50;
        const hasDistinctIncomingDate = Boolean(
            nextSeparator && !areDateIdentitiesEqual(activeSeparator, nextSeparator)
        );
        const hasIncomingPill = Boolean(hasDistinctIncomingDate && normalizedPushOffsetPx > 0);
        // Keep active inline separator hidden while sticky pill is visible to guarantee no duplicate date pill.
        const shouldHideInlineActiveSeparator = Boolean(activeSeparator?.dateKey);
        const shouldHideInlineNextSeparator = Boolean(
            hasIncomingPill
            && nextSeparator?.dateKey
            && isSeparatorOverlappingStickyLane({
                separator: nextSeparator,
                scrollTop,
                stickyTop: normalizedStickyTop,
                pillHeight: normalizedPillHeight,
            })
        );
        const nextState = {
            activeIndex,
            activeDateKey: activeSeparator.dateKey || "",
            activeLabel: activeSeparator.label || "",
            activeIso: activeSeparator.isoDate || "",
            shouldHideInlineActiveSeparator,
            nextDateKey: nextSeparator?.dateKey || "",
            nextLabel: nextSeparator?.label || "",
            nextIso: nextSeparator?.isoDate || "",
            shouldHideInlineNextSeparator,
            pushOffsetPx: normalizedPushOffsetPx,
            activeDockProgress,
        };

        setStickyState((previous) => (areStateValuesEqual(previous, nextState) ? previous : nextState));

        updateHiddenSeparatorClassNames({
            activeSeparator,
            nextSeparator,
            shouldHideInlineActiveSeparator,
            shouldHideInlineNextSeparator,
            scrollTop,
            stickyTop: normalizedStickyTop,
            pillHeight: normalizedPillHeight,
        });
    }, [
        clearHiddenSeparatorClassNames,
        pillHeightPx,
        scrollContainer,
        scrollRef,
        stickyTopPx,
        updateHiddenSeparatorClassNames,
    ]);

    const scheduleStickyStateUpdate = useCallback(() => {
        if (typeof window === "undefined") {
            return;
        }
        if (scrollRafRef.current !== null) {
            return;
        }

        scrollRafRef.current = window.requestAnimationFrame(() => {
            scrollRafRef.current = null;
            updateStickyState();
        });
    }, [updateStickyState]);

    const recalculateSeparatorOffsets = useCallback(() => {
        const container = resolveScrollContainer(scrollRef, scrollContainer);

        if (!container) {
            separatorsRef.current = [];
            separatorsByIdentityRef.current = new Map();
            clearHiddenSeparatorClassNames();
            setStickyState((previous) => (areStateValuesEqual(previous, EMPTY_STICKY_STATE) ? previous : EMPTY_STICKY_STATE));
            return;
        }

        const separators = pickDateSeparatorNodes(container)
            .map((node, separatorIndex) => {
                const meta = readDateSeparatorMetadata(node);
                if (!meta.isoDate && !meta.label) {
                    return null;
                }
                const measuredPlacement = measureSeparatorPlacement(node, DEFAULT_PILL_HEIGHT_PX);
                return {
                    node,
                    top: measuredPlacement.top,
                    height: measuredPlacement.height,
                    isoDate: meta.isoDate || "",
                    label: meta.label || "",
                    dateKey: buildSeparatorDateKey(meta.isoDate, meta.label, separatorIndex),
                };
            })
            .filter(Boolean)
            .sort((left, right) => left.top - right.top);

        const separatorsByIdentity = new Map();
        separators.forEach((separator) => {
            const identityKey = buildDateIdentityKey(separator);
            if (!identityKey) {
                return;
            }
            const previous = separatorsByIdentity.get(identityKey);
            if (previous) {
                previous.push(separator);
            } else {
                separatorsByIdentity.set(identityKey, [separator]);
            }
        });

        separatorsByIdentityRef.current = separatorsByIdentity;

        if (areSeparatorMeasurementsEqual(separatorsRef.current, separators)) {
            updateStickyState();
            return;
        }

        separatorsRef.current = separators;
        updateStickyState();
    }, [clearHiddenSeparatorClassNames, scrollContainer, scrollRef, updateStickyState]);

    const scheduleStickyRecalculation = useCallback(() => {
        if (typeof window === "undefined") {
            return;
        }
        if (recalcRafRef.current !== null) {
            return;
        }

        recalcRafRef.current = window.requestAnimationFrame(() => {
            recalcRafRef.current = null;
            recalculateSeparatorOffsets();
        });
    }, [recalculateSeparatorOffsets]);

    const scheduleStickyRecalculationAfterScrollIdle = useCallback(() => {
        if (typeof window === "undefined") {
            return;
        }
        if (scrollIdleTimeoutRef.current !== null) {
            window.clearTimeout(scrollIdleTimeoutRef.current);
        }
        // Re-measure after scroll settles so virtualized height corrections do not cause stale separator offsets.
        scrollIdleTimeoutRef.current = window.setTimeout(() => {
            scrollIdleTimeoutRef.current = null;
            scheduleStickyRecalculation();
        }, 120);
    }, [scheduleStickyRecalculation]);

    const measurePillHeight = useCallback(() => {
        const node = activePillRef.current;
        if (!node) {
            return;
        }

        const nextHeight = node.getBoundingClientRect().height;
        if (!Number.isFinite(nextHeight) || nextHeight <= 0) {
            return;
        }

        setPillHeightPx((previous) => (Math.abs(previous - nextHeight) > 0.5 ? nextHeight : previous));
    }, []);

    useEffect(() => {
        const container = resolveScrollContainer(scrollRef, scrollContainer);
        if (!container) {
            return undefined;
        }

        const handleScroll = () => {
            scheduleStickyStateUpdate();
            scheduleStickyRecalculationAfterScrollIdle();
        };

        container.addEventListener("scroll", handleScroll, { passive: true });
        scheduleStickyStateUpdate();

        return () => {
            container.removeEventListener("scroll", handleScroll);
        };
    }, [scheduleStickyRecalculationAfterScrollIdle, scheduleStickyStateUpdate, scrollContainer, scrollRef]);

    useEffect(() => {
        const container = resolveScrollContainer(scrollRef, scrollContainer);
        if (!container || typeof window === "undefined") {
            return undefined;
        }

        const handleLayoutChange = () => scheduleStickyRecalculation();
        window.addEventListener("resize", handleLayoutChange);

        let resizeObserver;
        if (typeof ResizeObserver === "function") {
            resizeObserver = new ResizeObserver(handleLayoutChange);
            resizeObserver.observe(container);
        }

        return () => {
            window.removeEventListener("resize", handleLayoutChange);
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
        };
    }, [scheduleStickyRecalculation, scrollContainer, scrollRef]);

    useEffect(() => {
        if (typeof window === "undefined") {
            scheduleStickyRecalculation();
            return undefined;
        }

        let isDisposed = false;
        let firstRafId = null;
        let secondRafId = null;

        const queueRecalculation = () => {
            if (isDisposed) {
                return;
            }
            scheduleStickyRecalculation();
        };

        // Pre-warm offsets synchronously so first user scroll does not pay a cold measurement cost.
        scheduleStickyRecalculation();
        // Let mount-time scroll positioning and list virtualization settle before first measurement.
        firstRafId = window.requestAnimationFrame(() => {
            secondRafId = window.requestAnimationFrame(queueRecalculation);
        });

        return () => {
            isDisposed = true;
            if (firstRafId !== null) {
                window.cancelAnimationFrame(firstRafId);
            }
            if (secondRafId !== null) {
                window.cancelAnimationFrame(secondRafId);
            }
        };
    }, [scheduleStickyRecalculation, recalculateKey]);

    useEffect(() => {
        measurePillHeight();

        const node = activePillRef.current;
        if (!node || typeof ResizeObserver !== "function") {
            return undefined;
        }

        const resizeObserver = new ResizeObserver(() => measurePillHeight());
        resizeObserver.observe(node);

        return () => resizeObserver.disconnect();
    }, [measurePillHeight, stickyState.activeDateKey, stickyState.activeLabel, stickyState.activeIso]);

    useEffect(() => {
        scheduleStickyStateUpdate();
    }, [pillHeightPx, scheduleStickyStateUpdate]);

    useEffect(() => () => {
        if (typeof window !== "undefined") {
            if (scrollRafRef.current !== null) {
                window.cancelAnimationFrame(scrollRafRef.current);
                scrollRafRef.current = null;
            }
            if (recalcRafRef.current !== null) {
                window.cancelAnimationFrame(recalcRafRef.current);
                recalcRafRef.current = null;
            }
            if (scrollIdleTimeoutRef.current !== null) {
                window.clearTimeout(scrollIdleTimeoutRef.current);
                scrollIdleTimeoutRef.current = null;
            }
        }
        clearHiddenSeparatorClassNames();
    }, [clearHiddenSeparatorClassNames]);

    const activeDisplayLabel = useMemo(
        () => resolveStickyDateLabel({ isoDate: stickyState.activeIso, label: stickyState.activeLabel }),
        [stickyState.activeIso, stickyState.activeLabel]
    );
    const nextDisplayLabel = useMemo(
        () => resolveStickyDateLabel({ isoDate: stickyState.nextIso, label: stickyState.nextLabel }),
        [stickyState.nextIso, stickyState.nextLabel]
    );

    if (!activeDisplayLabel) {
        return null;
    }

    const activeTranslateY = -stickyState.pushOffsetPx;
    const nextTranslateY = pillHeightPx - stickyState.pushOffsetPx;
    const hasIncomingPill = Boolean(
        nextDisplayLabel
        && nextDisplayLabel !== activeDisplayLabel
        && (stickyState.nextIso || stickyState.nextLabel)
        && !areDateIdentitiesEqual(
            {
                isoDate: stickyState.activeIso,
                label: stickyState.activeLabel,
                dateKey: stickyState.activeDateKey,
            },
            {
                isoDate: stickyState.nextIso,
                label: stickyState.nextLabel,
                dateKey: stickyState.nextDateKey,
            }
        )
        && stickyState.pushOffsetPx > 0
    );

    const visiblePills = [
        {
            role: "active",
            dateKey: stickyState.activeDateKey || buildSeparatorDateKey(stickyState.activeIso, stickyState.activeLabel, stickyState.activeIndex),
            label: stickyState.activeLabel,
            isoDate: stickyState.activeIso,
            displayLabel: activeDisplayLabel,
            translateY: activeTranslateY,
            // Dark while docked in place, glass while floating over messages.
            dockProgress: stickyState.activeDockProgress,
        },
        ...(hasIncomingPill
            ? [{
                role: "next",
                dateKey: stickyState.nextDateKey || buildSeparatorDateKey(stickyState.nextIso, stickyState.nextLabel, stickyState.activeIndex + 1),
                label: stickyState.nextLabel,
                isoDate: stickyState.nextIso,
                displayLabel: nextDisplayLabel,
                translateY: nextTranslateY,
                // Incoming pill is still floating in from below -> keep it glassy.
                dockProgress: 0,
            }]
            : []),
    ];

    return (
        <div className={classNames("sticky-date-header", className)} aria-hidden="true">
            <div className="sticky-date-header__stack">
                {visiblePills.map((pill) => (
                    <div
                        key={pill.dateKey}
                        ref={pill.role === "active" ? setActivePillRef : null}
                        className={classNames("sticky-date-header__pill", `sticky-date-header__pill--${pill.role}`)}
                        data-sticky-date-key={pill.dateKey}
                        style={{
                            transform: `translate3d(-50%, ${pill.translateY}px, 0)`,
                            "--dock-progress": pill.dockProgress,
                        }}
                    >
                        <DateSeparator
                            variant="sticky"
                            label={pill.label}
                            isoDate={pill.isoDate}
                            displayLabel={pill.displayLabel}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
};

const elementPropType = typeof Element === "undefined" ? PropTypes.any : PropTypes.instanceOf(Element);

StickyDateHeader.propTypes = {
    scrollRef: PropTypes.shape({
        current: elementPropType,
    }),
    scrollContainer: elementPropType,
    className: PropTypes.string,
    stickyTopPx: PropTypes.number,
    recalculateKey: PropTypes.any,
};

export default StickyDateHeader;
